/**
 * MarkdownContent component - renders markdown with custom components.
 * This is shared by both Tutorials and Docs pages.
 */

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CodeBlock } from "#app/components/tutorials/CodeBlock";
import { type MockData } from "#app/lib/tutorials/parser";
import { cn } from "#app/utils/misc";

export interface MarkdownContentProps {
  content: string;
  mocks?: MockData[];
  completedCheckpoints?: string[];
  className?: string;
}

function extractMockId(
  _content: string,
  _codeIndex: number,
): string | undefined {
  // Mock IDs are currently provided via frontmatter mocks array (tutorials).
  return undefined;
}

export function MarkdownContent({
  content,
  mocks = [],
  completedCheckpoints: _completedCheckpoints = [],
  className,
}: MarkdownContentProps) {
  const slugify = (text: string): string => {
    return text
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  };

  // Ensure duplicate headings get stable, unique IDs (e.g. `use-cases`, `use-cases-2`).
  // This avoids duplicate `id`s in the DOM and duplicate React keys in "On this page".
  const headingIdCounts = new Map<string, number>();
  const headingToUniqueId = (text: string): string => {
    const base = slugify(text);
    const next = (headingIdCounts.get(base) ?? 0) + 1;
    headingIdCounts.set(base, next);
    return next === 1 ? base : `${base}-${next}`;
  };

  const components: Parameters<typeof ReactMarkdown>[0]["components"] = {
    // ReactMarkdown wraps fenced code blocks in a <pre>.
    // Our custom `code` renderer returns a block <div> (CodeBlock), which would produce invalid HTML:
    // <pre><div>...</div></pre> and can lead to hydration warnings.
    // Unwrap <pre> entirely and let CodeBlock handle layout.
    pre: ({ children }) => <>{children}</>,
    code({ className: codeClassName, children, ...props }: any) {
      const codeString = String(children).replace(/\n$/, "");
      const match = /language-(\w+)/.exec(codeClassName || "");
      const language = match ? match[1] : "text";
      // react-markdown v9 no longer passes `inline` to the `code` renderer.
      // Heuristic: a fenced block always has either a `language-*` class
      // (added by remark for ```lang fences) or a literal newline. If
      // neither is present, the token is inline.
      const inline = !match && !codeString.includes("\n");

      if (inline) {
        return (
          <code
            className="px-1 py-0.5 rounded bg-gray-100 text-sm font-mono dark:bg-zinc-800"
            {...props}
          >
            {children}
          </code>
        );
      }

      let mockId: string | undefined;
      const codeBlocks = content.match(/```[\s\S]*?```/g) || [];
      const currentBlockIndex = codeBlocks.findIndex((block) =>
        block.includes(codeString),
      );
      if (currentBlockIndex >= 0) {
        mockId = extractMockId(content, currentBlockIndex);
      }

      return (
        <CodeBlock
          code={codeString}
          language={language}
          mockId={mockId}
          mocks={mocks}
        />
      );
    },
    h1: ({ children }) => {
      const text = String(children);
      const id = headingToUniqueId(text);
      return (
        <h1
          id={id}
          className="text-4xl font-bold mt-8 mb-4 text-[#141A42] dark:text-zinc-100 scroll-m-20"
        >
          {children}
        </h1>
      );
    },
    h2: ({ children }) => {
      const text = String(children);
      const id = headingToUniqueId(text);
      return (
        <h2
          id={id}
          className="text-3xl font-semibold mt-8 mb-4 text-[#141A42] dark:text-zinc-100 scroll-m-20"
        >
          {children}
        </h2>
      );
    },
    h3: ({ children }) => {
      const text = String(children);
      const id = headingToUniqueId(text);
      return (
        <h3
          id={id}
          className="text-2xl font-semibold mt-6 mb-3 text-[#141A42] dark:text-zinc-100 scroll-m-20"
        >
          {children}
        </h3>
      );
    },
    h4: ({ children }) => {
      const text = String(children);
      const id = headingToUniqueId(text);
      return (
        <h4
          id={id}
          className="text-xl font-semibold mt-4 mb-2 text-[#141A42] dark:text-zinc-100 scroll-m-20"
        >
          {children}
        </h4>
      );
    },
    p: ({ children }) => (
      <p className="mb-4 leading-7 text-gray-700 dark:text-gray-300">
        {children}
      </p>
    ),
    ul: ({ children }) => (
      <ul className="list-disc list-inside mb-4 space-y-2 text-gray-700 ml-4 dark:text-gray-300">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal list-inside mb-4 space-y-2 text-gray-700 ml-4 dark:text-gray-300">
        {children}
      </ol>
    ),
    li: ({ children }) => <li className="leading-7">{children}</li>,
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-[#535EA4] pl-4 italic my-4 text-gray-600 dark:text-gray-400">
        {children}
      </blockquote>
    ),
    table: ({ children }) => (
      <div className="my-6 w-full overflow-x-auto">
        <table className="w-full border-collapse">{children}</table>
      </div>
    ),
    th: ({ children }) => (
      <th className="border border-gray-200 bg-gray-50 px-3 py-2 text-left text-sm font-semibold text-[#141A42] dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border border-gray-200 px-3 py-2 align-top text-sm text-gray-700 dark:border-zinc-700 dark:text-gray-300">
        {children}
      </td>
    ),
    a: ({ href, children }) => (
      <a
        href={href}
        className="text-[#535EA4] hover:text-[#5B66AA] underline underline-offset-2"
        target={href?.startsWith("http") ? "_blank" : undefined}
        rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
      >
        {children}
      </a>
    ),
    strong: ({ children }) => (
      <strong className="font-semibold text-[#141A42] dark:text-zinc-100">
        {children}
      </strong>
    ),
    em: ({ children }) => <em className="italic">{children}</em>,
  };

  return (
    <div className={cn("prose prose-slate max-w-none", className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
